// useUsageHistory Hook
// Custom hook for fetching usage history and events

import { useState, useEffect, useCallback } from 'react';
import { billingAPI, type UsageHistoryResponse, type UsageEvent } from '../api/billing';

import { logger } from '../utils/logger';

interface UseUsageHistoryParams {
  organizationId: string | undefined;
  startDate?: string;
  endDate?: string;
  service?: string;
}

interface UseUsageHistoryReturn {
  usageHistory: UsageHistoryResponse | null;
  events: UsageEvent[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  setFilters: (filters: { startDate?: string; endDate?: string; service?: string }) => void;
}

/**
 * Custom hook to fetch usage history
 * @param params - Organization ID and optional filters
 * @returns Usage history data, events, loading state, error, and filter functions
 */
export function useUsageHistory({
  organizationId,
  startDate,
  endDate,
  service,
}: UseUsageHistoryParams): UseUsageHistoryReturn {
  const [usageHistory, setUsageHistory] = useState<UsageHistoryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFiltersState] = useState({
    startDate,
    endDate,
    service,
  });

  // Get events from usage history
  const events = usageHistory?.usage_events || [];

  // Fetch usage history
  const fetchUsageHistory = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      logger.log('🔵 Fetching usage history for org:', organizationId, 'with filters:', filters);
      const data = await billingAPI.getUsageHistory(organizationId, {
        start_date: filters.startDate,
        end_date: filters.endDate,
        service: filters.service,
      });
      
      logger.log('✅ Usage history fetched:', data);
      setUsageHistory(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch usage history';
      logger.error('❌ Error fetching usage history:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [organizationId, filters]);

  // Initial fetch
  useEffect(() => {
    fetchUsageHistory();
  }, [fetchUsageHistory]);

  // Update filters
  const setFilters = useCallback((newFilters: { startDate?: string; endDate?: string; service?: string }) => {
    setFiltersState(prev => ({
      ...prev,
      ...newFilters,
    }));
  }, []);

  return {
    usageHistory,
    events,
    loading,
    error,
    refetch: fetchUsageHistory,
    setFilters,
  };
}

